import {
  Col,
  Row,
  Alert,
  OverlayTrigger,
  Table,
  Tooltip,
} from "react-bootstrap";
import { useId, type ReactNode } from "react";
import { Link } from "react-router-dom";
import type { AccountsController } from "../../../api/src/accounts/accounts.controller";
import type { UnpackResponse } from "../../../api/src/lib";
import type {
  RebalanceAccount,
  RebalanceMetrics,
  RebalanceType,
} from "../../../api/src/rebalances/rebalances.service";
import Loading from "../Loading";
import { useQueryAccount } from "../clients/account/lib";
import AllocationBar from "../components/AllocationBar/AllocationBar";
import ModelSourceBadge, {
  getModelSource,
} from "../components/ModelSourceBadge";
import type { ModelAssignmentStrategy } from "../../../api/src/firms/firms.service";
import AssetAllocationBarChart, {
  AssetAllocationSeries,
} from "../components/AssetAllocationBarChart";
import {
  deserializeAllocationDetailsPercents,
  useAuthenticatedPercentFetch,
} from "../clients/percents";
import { naLabel, noModelLabel } from "../lib/display";
import { formatCurrency, formatPercent } from "../lib/numbers";
import { calculatePositionsCash } from "../lib/security";
import { calculateExcludedSecuritiesPercentage } from "../lib/excludedSecurities";
import { useQueryModel } from "../models/lib";
import { formatShare, summarizeCashTarget } from "./cashTarget";
import { getRealizedGainLoss, getRiskAllocation } from "./lib";
import CashBalanceOverrideBadge from "./CashBalanceOverrideBadge";
import type { ConsoleRebalanceMetrics } from "./nullRebalanceMetrics";
import type { RiskAllocation } from "../../../api/src/securities/securities.service";

export type RebalanceAccountWithMetrics = RebalanceAccount &
  Required<Omit<RebalanceMetrics, "cashSettings">> &
  Pick<ConsoleRebalanceMetrics, "cashSettings">;

const LABEL_STYLE = {
  fontWeight: 500,
  color: "#6c757d",
  padding: "8px 12px",
} as const;
const SUB_LABEL_STYLE = {
  ...LABEL_STYLE,
  fontWeight: 400,
  paddingLeft: "28px",
} as const;
const VALUE_STYLE = { padding: "8px 12px" } as const;

/** `<percent of the account> · <dollars>`, such as `5% · $20,600`. */
const shareAndDollars = (dollars: number, accountValue: number) =>
  `${formatShare(accountValue > 0 ? dollars / accountValue : null)} · ${formatCurrency(dollars)}`;

/** One term of a worked sum: its sign, its dollars and what it is. */
type SumTerm = {
  sign: "" | "+" | "−" | "=";
  dollars: number | null;
  label: string;
};

/** `+` for a positive amount, `−` for a negative one. */
const signOf = (dollars: number): SumTerm["sign"] => (dollars < 0 ? "−" : "+");

/** Whole dollars by the rule of `formatCurrency`: a half goes toward +∞. */
const roundDollars = (dollars: number) => Math.round(dollars) || 0;

/**
 * The whole-dollar figure each line of a worked sum shows, signed as it adds
 * into the total (a `−` line negative), with "none" as $0. The total is the
 * round of its own dollars, the figure of its row; each term is rounded alone,
 * and the dollars the rounding lost or gained move, one at a time, to the term
 * that rounding moved the most the other way, so the shown terms add up to the
 * shown total. A `+` line never goes below $0 and a `−` line never above it.
 */
const roundTerms = (terms: SumTerm[]): (number | null)[] => {
  const exact = terms.map(({ sign, dollars }) =>
    dollars === null
      ? null
      : sign === "+"
        ? Math.abs(dollars)
        : sign === "−"
          ? -Math.abs(dollars)
          : dollars,
  );
  const shown = exact.map((dollars) =>
    dollars === null ? null : roundDollars(dollars),
  );
  const totalAt = terms.findIndex(({ sign }) => sign === "=");
  if (totalAt === -1 || shown[totalAt] === null) return shown;
  let gap =
    (shown[totalAt] ?? 0) -
    shown.reduce<number>(
      (sum, dollars, i) => (i === totalAt ? sum : sum + (dollars ?? 0)),
      0,
    );
  while (gap !== 0) {
    const step = gap > 0 ? 1 : -1;
    let best = -1;
    let bestError = -Infinity;
    shown.forEach((dollars, i) => {
      if (i === totalAt || dollars === null) return;
      const moved = dollars + step;
      if (terms[i].sign === "+" && moved < 0) return;
      if (terms[i].sign === "−" && moved > 0) return;
      // To the cent, so that float dust does not break a tie of two terms
      // that rounding moved alike; a tie goes to the first term.
      const error = Math.round(((exact[i] ?? 0) - dollars) * step * 100);
      if (error > bestError) {
        best = i;
        bestError = error;
      }
    });
    if (best === -1) break;
    shown[best] = (shown[best] ?? 0) + step;
    gap -= step;
  }
  return shown;
};

/** `$29,868`, or `−$1,200` with the minus sign U+2212 for a negative figure. */
const formatSigned = (dollars: number) =>
  `${dollars < 0 ? "−" : ""}${formatCurrency(Math.abs(dollars))}`;

/**
 * A value whose sum shows on hover (or keyboard focus): the value is
 * underlined dotted and its tooltip lays the terms out one per line, ending
 * in the total, so the advisor never adds the figures up themselves. The
 * lines show whole dollars that add up to the total, which is the figure of
 * the row (`roundTerms`); so a term can show $1 more or less than the same
 * figure elsewhere on the page, the adjusted cash of the Post-Trade Cash sum
 * against the Adjusted Cash row or the reported cash against the badge.
 * Each tooltip takes its own `id` (`useId`), as an account's summary shows
 * once per account on the review and explain pages; `testId` names it for
 * the tests.
 */
const WorkedValue = ({
  testId,
  terms,
  children,
}: {
  testId: string;
  terms: SumTerm[];
  children: ReactNode;
}) => {
  const id = useId();
  const shown = roundTerms(terms);
  return (
    <OverlayTrigger
      placement="left"
      overlay={
        <Tooltip id={id} data-testid={testId}>
          <table className="text-start">
            <tbody>
              {terms.map(({ sign, label }, i) => {
                const dollars = shown[i];
                return (
                  <tr
                    key={label}
                    style={
                      sign === "="
                        ? {
                            borderTop: "1px solid currentColor",
                            fontWeight: 600,
                          }
                        : undefined
                    }
                  >
                    <td className="pe-1">{sign === "=" ? "" : sign}</td>
                    <td className="text-end pe-2">
                      {dollars === null
                        ? "none"
                        : sign === "+" || sign === "−"
                          ? formatCurrency(Math.abs(dollars))
                          : formatSigned(dollars)}
                    </td>
                    <td>{label}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Tooltip>
      }
    >
      <span
        tabIndex={0}
        style={{ borderBottom: "1px dotted currentColor", cursor: "help" }}
      >
        {children}
      </span>
    </OverlayTrigger>
  );
};

/**
 * The Adjusted Cash row of an account with an intraday cash adjustment: the
 * cash the rebalance ran with. Hovering it shows the sum from the custodian's
 * cash at the time of the adjustment: $4,088 reported + $30,000 adjustment =
 * $34,088 adjusted cash.
 */
const AdjustedCashRow = ({
  cashBalanceOverride,
}: {
  cashBalanceOverride: NonNullable<RebalanceAccount["cashBalanceOverride"]>;
}) => (
  <tr data-testid="adjusted-cash-row">
    <td style={LABEL_STYLE}>Adjusted Cash</td>
    <td className="text-end" style={VALUE_STYLE}>
      <WorkedValue
        testId="adjusted-cash-working"
        terms={[
          {
            sign: "",
            dollars: cashBalanceOverride.reportedCashBalance,
            label: "reported",
          },
          {
            sign: signOf(cashBalanceOverride.adjustment),
            dollars: cashBalanceOverride.adjustment,
            label: "adjustment",
          },
          {
            sign: "=",
            dollars: cashBalanceOverride.amount,
            label: "adjusted cash",
          },
        ]}
      >
        {formatCurrency(cashBalanceOverride.amount)}
      </WorkedValue>
    </td>
  </tr>
);

/**
 * The Cash Target, Cash Split / Cash Sweep and Post-Trade Cash rows of the
 * per-account summary. The target is the account's effective cash setting
 * (`cashSettings` of the rebalance's account, `GET /v1/rebalances/:id?metrics=true`);
 * post-trade cash is the account's cash plus its sells less its buys, and the
 * split security's post-trade value is its position in `postRebalanceHoldings`.
 * For an account with an intraday cash adjustment, the cash it starts from is
 * the stored adjusted cash (`cashBalanceOverride.amount`), the cash the
 * rebalance ran with, not the custodian's cash at the time of the read
 * (`cashBalance`); the Cash (Est. Unsettled/Settled) row stays on the
 * custodian's cash, the Adjusted Cash row above shows the adjusted cash, and
 * hovering Post-Trade Cash shows its sum (adjusted cash + sells − buys) so
 * the ending cash plainly includes the adjustment.
 * When the cash settings were not loaded (`undefined`, the manual rebalance),
 * the target rows are left out rather than shown as "None"; Post-Trade Cash
 * needs only the account's cash and trades, so it stays.
 *
 * The target rows (Cash Target, Cash Split, Cash Sweep, Post-Trade <split
 * symbol>) show only for a `tolerance` rebalance, the one type whose target
 * the rebalancer takes from the cash setting
 * (`create_model_cash_target_portfolio`, `python/src/rebalance/portfolio.py`):
 * `raise_cash` and `spend_cash` move the account's cash by the amount, `tlh`
 * has no target and `manual` carries the advisor's own trades.
 */
const CashTargetRows = ({
  rebalanceAccount,
  rebalanceType,
  sellTotal,
  buyTotal,
}: {
  rebalanceAccount: RebalanceAccountWithMetrics;
  rebalanceType: RebalanceType;
  sellTotal: number;
  buyTotal: number;
}) => {
  const accountValue = rebalanceAccount.totalBalance ?? 0;
  const showTargetRows =
    rebalanceType === "tolerance" &&
    rebalanceAccount.cashSettings !== undefined;
  const target = showTargetRows
    ? summarizeCashTarget(rebalanceAccount.cashSettings, accountValue)
    : null;
  const adjustedCash = rebalanceAccount.cashBalanceOverride?.amount;
  const postTradeCash =
    (adjustedCash ?? rebalanceAccount.cashBalance ?? 0) + sellTotal - buyTotal;
  const splitSymbol = target?.split?.symbol.toUpperCase();
  const postTradeSplitHolding =
    splitSymbol === undefined
      ? undefined
      : (rebalanceAccount.postRebalanceHoldings ?? []).find(
          (holding) =>
            holding.security?.symbol?.trim().toUpperCase() === splitSymbol,
        );

  return (
    <>
      {showTargetRows && (
        <tr data-testid="cash-target-row">
          <td style={LABEL_STYLE}>Cash Target</td>
          <td className="text-end" style={VALUE_STYLE}>
            {target === null
              ? "None (cash held as-is)"
              : `${formatShare(target.ratio)} · ${formatCurrency(target.dollars)}`}
          </td>
        </tr>
      )}
      {target?.split != null && (
        <>
          <tr data-testid="cash-split-row">
            <td style={SUB_LABEL_STYLE}>
              Cash Split ({target.split.symbol}){" "}
              {formatShare(target.split.splitShare)}
            </td>
            <td className="text-end" style={VALUE_STYLE}>
              {shareAndDollars(target.split.splitDollars, accountValue)}
            </td>
          </tr>
          <tr data-testid="cash-sweep-row">
            <td style={SUB_LABEL_STYLE}>
              Cash Sweep {formatShare(target.split.sweepShare)}
            </td>
            <td className="text-end" style={VALUE_STYLE}>
              {shareAndDollars(target.split.sweepDollars, accountValue)}
            </td>
          </tr>
        </>
      )}
      <tr data-testid="post-trade-cash-row">
        <td style={LABEL_STYLE}>Post-Trade Cash</td>
        <td className="text-end" style={VALUE_STYLE}>
          {adjustedCash == null ? (
            shareAndDollars(postTradeCash, accountValue)
          ) : (
            <WorkedValue
              testId="post-trade-cash-working"
              terms={[
                { sign: "", dollars: adjustedCash, label: "adjusted cash" },
                { sign: "+", dollars: sellTotal, label: "sells" },
                { sign: "−", dollars: buyTotal, label: "buys" },
                { sign: "=", dollars: postTradeCash, label: "post-trade cash" },
              ]}
            >
              {shareAndDollars(postTradeCash, accountValue)}
            </WorkedValue>
          )}
        </td>
      </tr>
      {target?.split != null && postTradeSplitHolding !== undefined && (
        <tr data-testid="post-trade-split-row">
          <td style={SUB_LABEL_STYLE}>Post-Trade {target.split.symbol}</td>
          <td className="text-end" style={VALUE_STYLE}>
            {shareAndDollars(postTradeSplitHolding.value ?? 0, accountValue)}
          </td>
        </tr>
      )}
    </>
  );
};

// Helper function to check if all risk allocation values are zero
const isRiskAllocationEmpty = (
  riskAllocation: RiskAllocation | undefined | null,
) => {
  if (!riskAllocation) return true;
  const excludeKeys = ["Cash", "Unknown", "cash", "unknown"];
  const riskValues = Object.entries(riskAllocation)
    .filter(([key]) => !excludeKeys.includes(key))
    .map(([, value]) => value);
  return riskValues.length === 0 || riskValues.every((v) => !v);
};

// Helper function to check if asset allocation data is empty (all allocations are zero)
const isAssetAllocationEmpty = (data: AssetAllocationSeries["data"]) => {
  return (
    !data ||
    data.length === 0 ||
    data.every((point) => !point || point.allocation === 0)
  );
};

interface RebalanceAccountInsightsProps {
  rebalanceAccount: RebalanceAccountWithMetrics;
  rebalanceType: RebalanceType;
  householdModelId?: number | null;
  modelAssignmentStrategy?: ModelAssignmentStrategy | null;
  showCalculatedTargetForResidual?: boolean;
}

const RebalanceAccountInsights = ({
  rebalanceAccount,
  rebalanceType,
  householdModelId,
  modelAssignmentStrategy = "fallback",
  showCalculatedTargetForResidual = false,
}: RebalanceAccountInsightsProps) => {
  const {
    isPending: isPendingAccount,
    isError: isErrorAccount,
    data: account,
  } = useQueryAccount(rebalanceAccount.id);

  const {
    isLoading: isLoadingModelAccount,
    isError: isErrorModelAccount,
    data: dataModelAccount,
  } = useQueryModel(account?.assignedModelId ?? -1, {
    enabled: typeof account !== "undefined" && account.assignedModelId != null,
  });

  const {
    isLoading: isLoadingModelHousehold,
    isError: isErrorModelHousehold,
    data: dataModelHousehold,
  } = useQueryModel(householdModelId ?? -1, {
    enabled: account?.assignedModelId == null && householdModelId != null,
  });

  const {
    isPending: isPendingAccountAllocation,
    isError: isErrorAccountAllocation,
    data: dataAccountAllocation,
  } = useAuthenticatedPercentFetch<
    UnpackResponse<AccountsController["getAccountAllocationDetails"]>
  >(
    `/v1/accounts/${rebalanceAccount.id}/allocation-details`,
    deserializeAllocationDetailsPercents,
    undefined,
    { enabled: true },
  );

  const buyTotal = rebalanceAccount.trades
    .filter((trade) => trade.side === "buy")
    .reduce((sum, trade) => sum + trade.amount * trade.price, 0);

  const sellTotal = rebalanceAccount.trades
    .filter((trade) => trade.side === "sell" || trade.side === "sell_all")
    .reduce((sum, trade) => sum + trade.amount * trade.price, 0);

  const realizedGainLoss = getRealizedGainLoss(rebalanceAccount);

  const settledCash = calculatePositionsCash(account?.holdings ?? []);
  const estimatedUnsettledCash = rebalanceAccount.cashBalance ?? 0;
  // The intraday cash adjustment the proposal ran with, if any. A response
  // from before the key existed carries none.
  const cashBalanceOverride = rebalanceAccount.cashBalanceOverride ?? null;

  // Calculate percentage of excluded securities for this account
  const excludedSecuritiesPercentage = account
    ? calculateExcludedSecuritiesPercentage(account)
    : 0;

  const isResidualAccount = account?.assignedModelId == null;
  const showTargetSeries =
    !isAssetAllocationEmpty(dataAccountAllocation?.data.target ?? []) &&
    (!isResidualAccount || showCalculatedTargetForResidual);

  // Create asset allocation series for this account. Residual accounts only
  // show the target when the caller opts in (Explains view) — there it is
  // labeled "Target (Calc)" to signal it's a derived value, matching the
  // completion-strategy convention. On the Trades view the target is
  // suppressed for residuals since the inherited/derived value is misleading
  // without the explanatory context.
  const assetAllocationSeries: AssetAllocationSeries[] = [
    {
      label: "Current",
      data: dataAccountAllocation?.data.current ?? [],
    },
    {
      label: "Post-Rebalance",
      data: rebalanceAccount.assetAllocation ?? [],
    },
    ...(showTargetSeries
      ? [
          {
            label: isResidualAccount ? "Target (Calc)" : "Target",
            data: dataAccountAllocation?.data.target ?? [],
          },
        ]
      : []),
  ];

  if (isPendingAccount) return <Loading />;
  if (isErrorAccount) return <div>An error occurred loading account</div>;
  if (!account) return <div>Account not found</div>;

  return (
    <>
      {/* Account Header */}
      <div className="account-header mb-4 pb-3">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h5 className="mb-1">
              <Link to={`/clients/accounts/${account.id}`}>
                {account.displayName} ({account.displayNumber})
              </Link>
            </h5>
            <small className="text-muted">
              {account?.type} •{" "}
              {account.assignedModelId == null && householdModelId == null ? (
                <>
                  {modelAssignmentStrategy === "completion" ? (
                    <>
                      <span className="fst-italic">Calculated Target</span>
                      <ModelSourceBadge
                        source="residual"
                        strategy={modelAssignmentStrategy}
                      />
                    </>
                  ) : (
                    <>
                      {noModelLabel}
                      <ModelSourceBadge source="none" />
                    </>
                  )}
                </>
              ) : account.assignedModelId == null ? (
                isLoadingModelHousehold ? (
                  <Loading />
                ) : isErrorModelHousehold ? (
                  <span className="text-danger">Failed to load model</span>
                ) : typeof dataModelHousehold === "undefined" ? (
                  noModelLabel
                ) : (
                  <>
                    <Link
                      to={`/models/models/${dataModelHousehold.id}`}
                      className="font-italic"
                    >
                      {dataModelHousehold.name}
                    </Link>
                    <ModelSourceBadge source="household" />
                  </>
                )
              ) : isLoadingModelAccount ? (
                <Loading />
              ) : isErrorModelAccount ? (
                <span className="text-danger">Failed to load model</span>
              ) : typeof dataModelAccount === "undefined" ? (
                noModelLabel
              ) : (
                <>
                  <Link to={`/models/models/${dataModelAccount.id}`}>
                    {dataModelAccount.name}
                  </Link>
                  <ModelSourceBadge source="account" />
                </>
              )}
            </small>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "auto auto",
              gap: "0.25rem 0.5rem",
              justifyContent: "end",
            }}
          >
            <small className="text-muted text-end">Total AUM:</small>
            <span className="h6 mb-0">
              {formatCurrency(rebalanceAccount.totalBalance ?? 0)}
            </span>
            <small className="text-muted text-end">Last Trade:</small>
            <span className="h6 mb-0">
              {rebalanceAccount.lastTradeDate === null
                ? naLabel
                : new Date(rebalanceAccount.lastTradeDate).toLocaleDateString()}
            </span>
          </div>
        </div>
        {cashBalanceOverride !== null && (
          <div className="mt-2">
            <CashBalanceOverrideBadge
              adjustment={cashBalanceOverride.adjustment}
              amount={cashBalanceOverride.amount}
              reportedCashBalance={cashBalanceOverride.reportedCashBalance}
              username={cashBalanceOverride.username}
              reason={cashBalanceOverride.reason}
            />
          </div>
        )}
      </div>

      {/* Risk Allocation */}
      <Row className="mb-3">
        <Col md={6}>
          <div className="mb-3 d-flex align-items-end">
            <div
              style={{
                width: "120px",
                paddingRight: "12px",
                textAlign: "right",
              }}
            >
              <small className="text-muted">Current</small>
            </div>
            <div style={{ flex: "1" }}>
              <AllocationBar
                data={rebalanceAccount.riskAllocation?.current}
                converter={getRiskAllocation}
              />
            </div>
          </div>
          {!isRiskAllocationEmpty(
            rebalanceAccount.riskAllocation?.postRebalance,
          ) && (
            <div className="mb-3 d-flex align-items-end">
              <div
                style={{
                  width: "120px",
                  paddingRight: "12px",
                  textAlign: "right",
                }}
              >
                <small className="text-muted">Post-Rebalance</small>
              </div>
              <div style={{ flex: "1" }}>
                <AllocationBar
                  data={rebalanceAccount.riskAllocation?.postRebalance}
                  converter={getRiskAllocation}
                />
              </div>
            </div>
          )}
          {/*
            Residual accounts render the Target bar only when the caller opts
            in (Explains view), where it's labeled "(Calc)" to signal the
            value is derived. On the Trades view the target is suppressed for
            residuals so the inherited/calculated value isn't read as the
            account's own target without supporting context.
          */}
          {!isRiskAllocationEmpty(rebalanceAccount.riskAllocation?.target) &&
            (!isResidualAccount || showCalculatedTargetForResidual) && (
              <div className="mb-3 d-flex align-items-end">
                <div
                  style={{
                    width: "120px",
                    paddingRight: "12px",
                    textAlign: "right",
                  }}
                >
                  <small className="text-muted">
                    Target
                    {isResidualAccount && (
                      <span className="fst-italic"> (Calc)</span>
                    )}
                  </small>
                </div>
                <div style={{ flex: "1" }}>
                  <AllocationBar
                    data={rebalanceAccount.riskAllocation?.target}
                    converter={getRiskAllocation}
                  />
                </div>
              </div>
            )}

          {/* Excluded securities annotation */}
          {!isRiskAllocationEmpty(rebalanceAccount.riskAllocation?.target) &&
            (!isResidualAccount || showCalculatedTargetForResidual) &&
            excludedSecuritiesPercentage > 0 && (
              <div className="text-muted small fst-italic mt-2">
                *Excluded securities (
                {formatPercent(excludedSecuritiesPercentage / 100, 0)} of
                holdings) are not calculated in the target allocation.
              </div>
            )}

          {/* Cash Flow and Trading Metrics Table */}
          <div className="mt-4">
            <Table size="sm" borderless className="mb-0">
              <tbody>
                <tr>
                  <td
                    style={{
                      width: "250px",
                      fontWeight: 500,
                      color: "#6c757d",
                      padding: "8px 12px",
                    }}
                  >
                    Cash (Est. Unsettled/Settled)
                  </td>
                  <td className="text-end" style={{ padding: "8px 12px" }}>
                    {formatCurrency(estimatedUnsettledCash)} /{" "}
                    {formatCurrency(settledCash)}
                  </td>
                </tr>
                {cashBalanceOverride !== null && (
                  <AdjustedCashRow cashBalanceOverride={cashBalanceOverride} />
                )}
                <CashTargetRows
                  rebalanceAccount={rebalanceAccount}
                  rebalanceType={rebalanceType}
                  sellTotal={sellTotal}
                  buyTotal={buyTotal}
                />
                <tr>
                  <td
                    style={{
                      fontWeight: 500,
                      color: "#6c757d",
                      padding: "8px 12px",
                    }}
                  >
                    Sell
                  </td>
                  <td className="text-end" style={{ padding: "8px 12px" }}>
                    {formatCurrency(sellTotal)}
                  </td>
                </tr>
                <tr>
                  <td
                    style={{
                      fontWeight: 500,
                      color: "#6c757d",
                      padding: "8px 12px",
                    }}
                  >
                    Buy
                  </td>
                  <td className="text-end" style={{ padding: "8px 12px" }}>
                    {formatCurrency(buyTotal)}
                  </td>
                </tr>
                <tr style={{ borderTop: "1px solid #dee2e6" }}>
                  <td
                    style={{
                      fontWeight: 500,
                      color: "#495057",
                      padding: "8px 12px",
                    }}
                  >
                    Net
                  </td>
                  <td
                    className="text-end"
                    style={{
                      padding: "8px 12px",
                      fontWeight: 500,
                      color: sellTotal - buyTotal >= 0 ? "#28a745" : "#dc3545",
                    }}
                  >
                    {formatCurrency(sellTotal - buyTotal)}
                  </td>
                </tr>
                <tr>
                  <td
                    style={{
                      fontWeight: 500,
                      color: "#6c757d",
                      padding: "8px 12px",
                    }}
                  >
                    Gains
                  </td>
                  <td
                    className="text-end"
                    style={{
                      padding: "8px 12px",
                      color:
                        (realizedGainLoss ?? 0) >= 0 ? "#28a745" : "#dc3545",
                    }}
                  >
                    {formatCurrency(realizedGainLoss)}
                  </td>
                </tr>
                {rebalanceType === "raise_cash" ||
                rebalanceType === "spend_cash" ? (
                  <tr>
                    <td
                      style={{
                        fontWeight: 500,
                        color: "#6c757d",
                        padding: "8px 12px",
                      }}
                    >
                      {rebalanceType === "raise_cash"
                        ? "Raise Cash"
                        : "Spend Cash"}
                    </td>
                    <td className="text-end" style={{ padding: "8px 12px" }}>
                      {formatCurrency(
                        rebalanceAccount.raiseSpendCashAmount ?? 0,
                      )}
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </Table>
          </div>
        </Col>
        <Col md={6}>
          <div>
            {isPendingAccountAllocation ? (
              <Loading />
            ) : isErrorAccountAllocation ? (
              <Alert variant="danger">Failed to load asset allocation</Alert>
            ) : (
              <AssetAllocationBarChart
                data={assetAllocationSeries}
                excludedSecuritiesPercentage={excludedSecuritiesPercentage}
              />
            )}
          </div>
        </Col>
      </Row>
    </>
  );
};

export default RebalanceAccountInsights;
